import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useSessionSocket, useCountdown } from "../../lib/ws";
import { Avatar } from "../../components/AvatarBuilder";
import { ChallengeAggregations } from "../../components/ChallengeAggregations";
import LecturerNotesPanel from "../../components/LecturerNotesPanel";
import { findQuestionContext, getTopics, getFlatQuestions } from "../../lib/workshop";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";

export default function LiveSession() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [workshop, setWorkshop] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [results, setResults] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [openSlides, setOpenSlides] = useState({});

  const refresh = useCallback(async () => {
    try {
      const { data: s } = await api.get(`/sessions/${sessionId}`);
      setSession(s);
      const { data: w } = await api.get(`/workshops/${s.workshop_id}`);
      setWorkshop(w);
      const { data: p } = await api.get(`/sessions/${sessionId}/participants`);
      setParticipants(p);
      if (s.current_challenge_id) {
        const { data: r } = await api.get(`/sessions/${sessionId}/results/${s.current_challenge_id}`);
        setResults(r);
      }
    } catch (e) {
      if (e.response?.status === 401) navigate("/teacher/login");
    }
  }, [sessionId, navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  useSessionSocket(sessionId, (msg) => {
    if (["participant_joined", "participant_ready", "response_submitted", "challenge_started", "challenge_control", "workshop_switched", "projector_media"].includes(msg.type)) {
      refresh();
    }
  });

  useEffect(() => {
    if (!showQR) return;
    const onKey = (e) => e.key === "Escape" && setShowQR(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showQR]);

  const remaining = useCountdown(
    session?.current_challenge_started_at,
    session?.current_challenge_duration,
    session?.current_challenge_status
  );

  const startChallenge = async (challengeId) => {
    await api.post(`/sessions/${sessionId}/start-challenge`, { challenge_id: challengeId });
    toast.success("Challenge spuštěna");
  };

  const control = async (action, extra = 30) => {
    await api.post(`/sessions/${sessionId}/control`, { action, extra_seconds: extra });
    refresh();
  };

  const finishSession = async () => {
    if (!window.confirm("Opravdu ukončit session?")) return;
    await api.post(`/sessions/${sessionId}/finish`);
    toast.success("Session ukončena");
    refresh();
  };

  const setRevealMode = async (mode) => {
    try {
      await api.post(`/sessions/${sessionId}/awards-reveal`, { mode });
      toast.success(mode === "public" ? "Ocenění odhalena na projektoru" : "Ocenění zase soukromá");
      refresh();
    } catch (e) {
      toast.error("Nepodařilo se přepnout");
    }
  };

  const switchWorkshop = async (newWid) => {
    if (!confirm("Přepnout na další workshop? Aktuální výsledky tohoto workshopu zůstanou v reportu, ale metriky žáků se vynulují.")) return;
    try {
      await api.post(`/sessions/${sessionId}/switch-workshop`, { workshop_id: newWid });
      toast.success("Přepnuto. Žáci zůstávají připojeni.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Přepnutí selhalo");
    }
  };

  const setProjectorMedia = async (topicId, slideId = null) => {
    try {
      await api.post(`/sessions/${sessionId}/projector-media`, { topic_id: topicId || null, slide_id: slideId });
      toast.success(topicId ? "Zobrazeno na projektoru" : "Skryto z projektoru");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nepodařilo se");
    }
  };

  if (!session || !workshop) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Načítám...</div>;
  }

  const currentCh = getFlatQuestions(workshop).find((c) => c.id === session.current_challenge_id);
  const ctx = findQuestionContext(workshop, session.current_challenge_id);
  const topics = getTopics(workshop);
  const flatQuestions = getFlatQuestions(workshop);
  const responded = results?.total_responses || 0;
  const ready = participants.filter((p) => p.avatar_config).length;
  const doneIds = new Set((session.challenge_history || []).map((h) => h.challenge_id));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/teacher" className="text-slate-400 hover:text-slate-900 text-sm font-bold">← Zpět</Link>
            <div>
              <div className="flex items-center gap-2">
                <div className="font-heading font-black text-slate-900 tracking-tight">{session.title}</div>
                {session.safety_mode && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200" title="Safety mode aktivní">
                    🛡️ Safety mode
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500">{session.workshop_title}</div>
              {(session.workshop_queue?.length > 0 || session.workshop_history?.length > 0) && (
                <div className="text-[10px] text-slate-400 mt-0.5" data-testid="playlist-meta">
                  {session.workshop_history?.length > 0 && `${session.workshop_history.length} odehráno · `}
                  {session.workshop_queue?.length > 0 && `${session.workshop_queue.length} ve frontě`}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="px-3 py-1.5 bg-slate-900 text-white font-mono font-bold rounded-md" data-testid="session-code">
              {session.code}
            </div>
            <button
              onClick={() => setShowQR(true)}
              className="text-sm font-bold text-blue-600 hover:underline"
              data-testid="btn-show-qr"
            >
              QR ↗
            </button>
            <a
              href={`/projector/${session.code}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-bold text-blue-600 hover:underline"
              data-testid="link-projector"
            >
              Projektor ↗
            </a>
            {session.status === "finished" && (
              <a
                href={`/teacher/session/${session.id}/report`}
                className="text-sm font-bold text-green-700 hover:underline"
                data-testid="link-report"
              >
                Report ↗
              </a>
            )}
            <button
              onClick={finishSession}
              disabled={session.status === "finished"}
              className="text-sm font-bold text-red-600 hover:bg-red-50 px-3 py-1 rounded-md disabled:opacity-50"
              data-testid="btn-finish-session"
            >
              Ukončit
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
        {/* Left: challenge control + workshop */}
        <div className="space-y-6">
          {/* Playlist queue panel */}
          {Array.isArray(session.workshop_queue) && session.workshop_queue.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="playlist-queue">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-black uppercase tracking-widest text-blue-700">📚 Ve frontě</div>
                  <div className="text-sm text-slate-800 mt-0.5">
                    Po dokončení tohoto workshopu můžeš plynule přejít na další. Žáci se nepřipojují znovu.
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {session.workshop_queue.map((wid) => (
                    <button
                      key={wid}
                      onClick={() => switchWorkshop(wid)}
                      className="bg-white hover:bg-blue-100 border border-blue-300 text-blue-900 font-bold rounded-md px-3 py-1.5 text-xs"
                      data-testid={`btn-switch-${wid}`}
                    >
                      → {session.workshop_queue.indexOf(wid) + 1}. {wid.replace(/^ws-/, "").replace(/-v\d+$/, "")}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Awards reveal panel (only after finish) */}
          {session.status === "finished" && (
            <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="awards-reveal-panel">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-amber-600 mb-1">🏆 Ocenění třídy</div>
                  <div className="font-heading font-black text-lg tracking-tight text-slate-900">
                    {session.awards_reveal_mode === "public" ? "Veřejně promítáme na projektoru" : "Zatím soukromě"}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    {session.awards_reveal_mode === "public"
                      ? "Ceremoniál hraje na projektoru. Přepni zpět do soukromého módu, pokud chceš."
                      : "Studenti vidí jen svoje ocenění. Klikni níž, abys ceremoniál pustil/a celé třídě."}
                  </p>
                </div>
                {session.awards_reveal_mode === "public" ? (
                  <button
                    onClick={() => setRevealMode("private")}
                    className="bg-white border border-slate-300 hover:border-slate-900 text-slate-900 font-bold rounded-md px-4 py-2 text-sm flex-shrink-0"
                    data-testid="btn-hide-awards"
                  >
                    Skrýt z projektoru
                  </button>
                ) : (
                  <button
                    onClick={() => setRevealMode("public")}
                    className="bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-md px-4 py-2 text-sm flex-shrink-0"
                    data-testid="btn-reveal-awards"
                  >
                    🎬 Spustit ceremoniál
                  </button>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 leading-relaxed">
                💡 Tip: ve „Hořící nervy" a „Náročný den" se občas ocitne i citlivý žák. Pokud máš ve třídě někoho v krizi, nech to v <strong>soukromém módu</strong> — každý si svoje uvidí jen na svém telefonu.
              </div>
            </div>
          )}

          {/* Current challenge */}
          {currentCh ? (
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-blue-600 mb-1">
                    {session.current_challenge_status === "running" ? "🔴 Běží" : session.current_challenge_status === "paused" ? "⏸ Pozastaveno" : "Připraveno"}
                  </div>
                  {ctx && (
                    <div className="text-[11px] text-slate-500 mb-1" data-testid="topic-breadcrumb">
                      Téma {ctx.topicIndex}/{ctx.totalTopics} · <span className="font-bold text-slate-700">{ctx.topic.title}</span>
                      {" · "}Otázka {ctx.questionIndex}/{ctx.totalQuestions}
                    </div>
                  )}
                  <h2 className="text-xl font-heading font-black tracking-tight text-slate-900">{currentCh.title}</h2>
                  <p className="text-sm text-slate-500 mt-1">{currentCh.context}</p>
                </div>
                <div className="text-right">
                  <div className={`font-mono font-bold text-2xl ${remaining <= 10 ? "text-red-600" : "text-slate-900"}`} data-testid="teacher-timer">
                    {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, "0")}
                  </div>
                  <div className="text-xs text-slate-500">zbývá</div>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full bg-blue-600 transition-all"
                    style={{ width: `${participants.length ? (responded / participants.length) * 100 : 0}%` }}
                  />
                </div>
                <div className="text-sm font-bold text-slate-900 tabular-nums">
                  {responded} / {participants.length}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {session.current_challenge_status === "running" ? (
                  <button onClick={() => control("pause")} className="bg-slate-900 text-white px-3 py-2 rounded-md text-sm font-bold" data-testid="btn-pause">⏸ Pauza</button>
                ) : session.current_challenge_status === "paused" ? (
                  <button onClick={() => control("resume")} className="bg-blue-600 text-white px-3 py-2 rounded-md text-sm font-bold" data-testid="btn-resume">▶ Pokračovat</button>
                ) : null}
                <button onClick={() => control("extend", 30)} className="bg-white border border-slate-200 px-3 py-2 rounded-md text-sm font-bold" data-testid="btn-extend">+30s</button>
                <button onClick={() => control("skip")} className="bg-white border border-slate-200 px-3 py-2 rounded-md text-sm font-bold text-red-600" data-testid="btn-skip">Přeskočit</button>
              </div>

              {results && (
                <div className="mt-5 pt-5 border-t border-slate-200">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Odpovědi (agregace)</div>
                  <ChallengeAggregations challenge={currentCh} results={results} responded={responded} />
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
              <div className="text-3xl mb-2">🎯</div>
              <div className="font-heading font-black text-lg text-slate-900">Vyber první challenge</div>
              <div className="text-sm text-slate-500">Klikni dole na challenge kterou chceš spustit.</div>
            </div>
          )}

          {/* Lecturer methodology panel — visible only to the teacher */}
          {currentCh && (
            <LecturerNotesPanel
              sessionId={sessionId}
              challenge={currentCh}
              topicContext={ctx}
              topic={ctx?.topic}
            />
          )}

          {/* Workshop plan — grouped by topic */}
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Plán workshopu · {topics.length} téma{topics.length === 1 ? "" : topics.length < 5 ? "ta" : "t"} · {flatQuestions.length} otázka{flatQuestions.length === 1 ? "" : flatQuestions.length < 5 ? "y" : ""}
              </div>
            </div>
            <div className="space-y-4">
              {topics.map((t, ti) => {
                const qs = t.questions || [];
                const topicDone = qs.every((q) => doneIds.has(q.id));
                const topicHasCurrent = qs.some((q) => q.id === session.current_challenge_id);
                return (
                  <div
                    key={t.id}
                    className={`border rounded-md ${topicHasCurrent ? "border-blue-400 bg-blue-50/40" : "border-slate-200"}`}
                    data-testid={`topic-row-${t.id}`}
                  >
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
                      <div className={`w-6 h-6 rounded-full font-bold text-[11px] flex items-center justify-center ${
                        topicDone ? "bg-green-500 text-white" : topicHasCurrent ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"
                      }`}>
                        {topicDone ? "✓" : ti + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-black text-sm text-slate-900 truncate">{t.title}</div>
                        <div className="text-[11px] text-slate-500">
                          {qs.length} otázka{qs.length === 1 ? "" : qs.length < 5 ? "y" : ""}
                          {t.intro ? ` · ${t.intro.slice(0, 60)}${t.intro.length > 60 ? "…" : ""}` : ""}
                        </div>
                      </div>
                      {(t.slides?.length > 0) && (
                        <button
                          onClick={() => setOpenSlides((o) => ({ ...o, [t.id]: !o[t.id] }))}
                          className="text-[11px] font-bold px-2.5 py-1.5 rounded-md flex-shrink-0 bg-slate-100 hover:bg-slate-200 text-slate-700"
                          data-testid={`btn-toggle-slides-${t.id}`}
                        >
                          {openSlides[t.id] ? "▾ Slajdy" : `▸ Slajdy & poznámky (${t.slides.length})`}
                        </button>
                      )}
                    </div>
                    {openSlides[t.id] && t.slides?.length > 0 && (
                      <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 space-y-2" data-testid={`slides-panel-${t.id}`}>
                        {t.slides.map((sl) => {
                          const active = session.projector_media?.topic_id === t.id && session.projector_media?.slide_id === sl.slide_id;
                          return (
                            <div key={sl.slide_id} className="bg-white border border-slate-200 rounded-md p-2.5" data-testid={`slide-${t.id}-${sl.slide_id}`}>
                              <div className="flex items-start gap-2">
                                <span className="text-[9px] font-black uppercase tracking-wide bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5">{slideTypeLabel(sl.type)}</span>
                                <div className="flex-1 min-w-0">
                                  <div className="font-bold text-xs text-slate-900">{sl.title}</div>
                                  {sl.text && <div className="text-[11px] text-slate-600 whitespace-pre-line mt-1">{sl.text}</div>}
                                </div>
                                <button
                                  onClick={() => setProjectorMedia(active ? null : t.id, active ? null : sl.slide_id)}
                                  className={`text-[10px] font-bold px-2 py-1 rounded flex-shrink-0 ${active ? "bg-brand-cyan text-white" : "bg-slate-900 hover:bg-slate-700 text-white"}`}
                                  data-testid={`btn-project-slide-${t.id}-${sl.slide_id}`}
                                >
                                  {active ? "⏹ Skrýt" : "📺 Na projektor"}
                                </button>
                              </div>
                              {sl.notes && (
                                <div className="mt-2 text-[11px] text-slate-700 bg-amber-50 border-l-2 border-amber-400 pl-2 py-1 whitespace-pre-line" data-testid={`slide-notes-${t.id}-${sl.slide_id}`}>
                                  <span className="font-bold text-amber-700">Poznámka: </span>{sl.notes}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="p-2 space-y-1">
                      {qs.map((c, qi) => {
                        const isCurrent = c.id === session.current_challenge_id;
                        const done = doneIds.has(c.id);
                        return (
                          <div
                            key={c.id}
                            className={`flex items-center gap-3 p-2 rounded-md border text-sm ${
                              isCurrent ? "border-blue-500 bg-blue-50" : "border-transparent hover:bg-slate-50"
                            }`}
                          >
                            <div className={`w-6 h-6 rounded-full font-bold text-[10px] flex items-center justify-center flex-shrink-0 ${
                              done ? "bg-green-500 text-white" : isCurrent ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"
                            }`}>
                              {done ? "✓" : `${ti + 1}.${qi + 1}`}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-sm text-slate-900 truncate">{c.title}</div>
                              <div className="text-[11px] text-slate-500">{typeLabel(c.type)} · {c.duration_seconds}s</div>
                            </div>
                            <button
                              onClick={() => startChallenge(c.id)}
                              disabled={session.status === "finished"}
                              className="text-xs font-bold bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-md disabled:opacity-40 flex-shrink-0"
                              data-testid={`btn-start-${c.id}`}
                            >
                              {isCurrent ? "Restart" : done ? "Znovu" : "▶ Spustit"}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {topics.length === 0 && (
                <div className="text-sm text-slate-400 text-center py-6">Workshop nemá žádná témata.</div>
              )}
            </div>
          </div>
        </div>

        {/* Right: participants panel */}
        <aside className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-baseline justify-between mb-3">
              <div className="font-heading font-black text-slate-900">Účastníci</div>
              <div className="text-xs text-slate-500">{ready}/{participants.length} hotovo</div>
            </div>
            {participants.length === 0 ? (
              <div className="text-center py-6 text-sm text-slate-500">
                Nikdo se zatím nepřipojil.<br/>
                <span className="font-mono font-bold text-slate-900 text-lg block mt-2">{session.code}</span>
              </div>
            ) : (
              <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                {participants.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-md">
                    {p.avatar_config ? (
                      <div style={{ background: `#${p.avatar_config.bg}` }} className="w-8 h-8 rounded-md overflow-hidden border border-slate-200">
                        <Avatar config={p.avatar_config} size={32} />
                      </div>
                    ) : (
                      <div className="w-8 h-8 rounded-md bg-slate-200 flex items-center justify-center text-slate-500 text-xs">?</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-slate-900 truncate">{p.nickname}</div>
                      <div className="text-xs text-slate-500 truncate">{p.avatar_config ? "Připraven" : "Tvoří postavu"}</div>
                    </div>
                    {p.recovery_code && (
                      <button
                        onClick={() => {
                          navigator.clipboard?.writeText(p.recovery_code);
                          toast.success(`Recovery kód ${p.recovery_code} zkopírován`);
                        }}
                        className="font-mono text-[11px] font-bold tabular-nums tracking-wider bg-white hover:bg-slate-100 border border-slate-200 rounded-md px-2 py-1 text-slate-700"
                        title="Recovery kód — klikni pro zkopírování"
                        data-testid={`recovery-${p.nickname}`}
                      >
                        🔑 {p.recovery_code}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      </main>

      {showQR && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowQR(false)}>
          <div className="bg-white rounded-lg p-8 max-w-md w-full text-center" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-black text-2xl tracking-tight text-slate-900 mb-1">Připojení třídy</h3>
            <p className="text-sm text-slate-500 mb-4">Žáci mohou naskenovat QR kód nebo zadat kód ručně.</p>
            <div className="bg-white p-4 rounded-md inline-block border-2 border-slate-900">
              <QRCodeSVG
                value={`${window.location.origin}/join/${session.code}`}
                size={240}
                bgColor="#FFFFFF"
                fgColor="#0F172A"
                level="M"
                includeMargin={false}
                data-testid="session-qr"
              />
            </div>
            <div className="mt-4 font-mono font-black text-3xl text-slate-900 tracking-widest">{session.code}</div>
            <div className="mt-1 text-xs text-slate-500 truncate">{window.location.origin}/join/{session.code}</div>
            <button
              onClick={() => setShowQR(false)}
              className="mt-5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-md px-5 py-2 text-sm"
            >
              Zavřít
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function typeLabel(t) {
  return {
    quick_decision: "Rychlá volba",
    multi_step: "Vícekroková",
    chat_simulation: "Chat",
    feed_analysis: "Feed",
    sequence: "Pořadí",
    vote: "Hlasování",
    reflection: "Reflexe",
    group_task: "Skupina",
  }[t] || t;
}

function slideTypeLabel(t) {
  return {
    intro: "Úvod",
    text: "Text",
    video: "Video",
    iframe: "Web",
    quiz_intro: "Kvíz",
  }[t] || t;
}
